<template>
  <b-modal
    id="recordingDetailsModal"
    ref="recordingDetailsModal"
    hide-footer
    hide-header
    size="xl"
  >
    <div class="big flex justify-center">
      {{ $t('soundRecordingDetails')|capitalize }}
    </div>

    <div class="p-4 input-container">
      <!-- Title -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('title')|capitalize }}:
        </div>
        <div :class="`${showRequired('title')}`">
          <input
            v-model="title"
            class="w100p custom-input"
            type="text"
          >
        </div>
      </div>

      <!-- Version Title -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('versionTitle')|capitalize }}:
        </div>
        <input
          v-model="versionTitle"
          class="w100p custom-input"
          type="text"
        >
      </div>

      <!-- Artist -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('artist')|capitalize }}:
        </div>
        <div :class="`${showRequired('artist')}`">
          <input
            v-model="artist"
            class="w100p custom-input"
            type="text"
          >
        </div>
      </div>

      <!-- ISRC -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('isrc')|upper }}:
        </div>
        <input
          v-model="isrc"
          class="w100p custom-input"
          type="text"
        >
      </div>

      <!-- Fixation Year -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('fixation_year')|capitalize }}:
        </div>
        <input
          v-model="fixationYear"
          class="w100p custom-input"
          type="text"
        >
      </div>

      <!-- Fixation Country -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('fixation_country') | capitalize }}:
        </div>

        <Multiselect
          v-model="fixationCountry"
          :options="countries"
          label="name"
          track-by="name"
          :multiple="false"
          :hide-selected="true"
          :show-labels="false"
          placeholder
        />
      </div>

      <!-- Duration -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ `${$t('duration')} (HH:mm:ss)` }}:
        </div>
        <input
          v-model="duration"
          class="w100p custom-input"
          type="text"
        >
      </div>

      <!-- Add Release Names -->
      <div class="w50p pr-4">
        <div class="text-md py-2">
          {{ $t('addAlbumName') | capitalize }}:
        </div>
        <div class="input-icon-wrap items-center">
          <input
            id="form-name"
            v-model="releaseName"
            type="text"
            class="input-with-icon"
            @keypress="addAlbumName"
          >
          <span
            class="input-icon"
            :class="{clickable: this.releaseName.length}"
            @click="addAlbumName"
          >
            <span class="fa fa-plus-circle" />
          </span>
        </div>
      </div>

      <!-- Type -->
      <div class="text-md w50p pr-4">
        <div class="text-md py-2">
          {{ $t('type')|capitalize }}:
        </div>

        <Multiselect
          v-model="type"
          :class="`${showRequired('type')}`"
          :options="srTypes"
          label="type"
          :multiple="false"
          :hide-selected="true"
          :show-labels="false"
          placeholder
        >
          <template v-slot:singleLabel="{ option }">
            <div class="form_block2 societies space-between">
              <div class="option__desc">
                <span class="option__title">{{ option.type | capitalize }}</span>
              </div>
            </div>
          </template>
        </Multiselect>
      </div>

      <!-- Release Names -->
      <div class="pr-4 w50p">
        <div class="text-md py-2">
          {{ $t('releaseNames')|capitalize }}:
        </div>

        <div class="badge-container">
          <b-badge
            v-for="(releaseName,i) in releaseNames"
            :key="`${releaseName}-${i}`"
            class="p-2 m-1 text-md"
          >
            <span>{{ releaseName }}</span>
            <i
              class="fas fa-times-circle clickable"
              @click="removeReleaseName(releaseName)"
            />
          </b-badge>
        </div>
      </div>

      <!-- Extras -->
      <div class="flex pt-5 w100p justify-content-between flex-row align-items-end">
        <div class="text-md">
          {{ $t('extras')|capitalize }}
        </div>
        <div class="pr-4">
          <a
            href="#"
            class="text-primary"
            @click.prevent="addExtra"
          >{{ $t('add') }}</a>
        </div>
      </div>

      <RecordingExtra
        v-for="(extra, index) in extras"
        :key="index"
        :extra="extra"
        :can-remove="extra.canRemove"
        class="w100p"
        @removeExtra="removeExtra(index)"
      />

      <!-- Is Eligible -->
      <div
        v-if="modalType==='usages' "
        class="pr-4 w50p"
      >
        <div class="checkbox-parent">
          <div class="checkbox-container">
            <div class="text-md py-2 mr-2">
              {{ $t('isEligible')|capitalize }}:
            </div>
            <div>
              <toggle-button
                v-model="eligible"
                color="#2ec775"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="w100p flex justify-end py-2">
      <div class="w50p">
        <div
          class="flex"
          style="margin-top:28px;"
        >
          <span
            class="btn px-4 py-2 w100p big mr-4 btn-primary"
            @click="saveRecord"
          >
            <spinner v-if="loading" />
            <span v-else>{{ $t('add') | capitalize }}</span>
          </span>

          <button
            class="modal-default-button py-2 w100p btn border"
            @click="$emit('close', $refs.recordingDetailsModal)"
          >
            {{ $t('dismiss')|capitalize }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss" scoped>
div {
  font-family: "CircularStd", sans-serif;
}

.required {
  border: solid 2px red;
  border-radius: 8px;
  box-sizing: border-box;
  z-index: 1000;
}

.input-container {
  display: flex;
  flex-wrap: wrap;
}

.badge-container {
  display: flex;
  min-height: 45px;
  padding: 2px;
  min-width: 160px;
  color: black;
  border: 1px solid #bec4cf;
  border-radius: 4px;
  flex-wrap: wrap;
}

.custom-input {
  height: 40px;
  padding: 6px;
  color: black;
  border: 1px solid #888e99;
  border-radius: 4px;
}

.input-icon-wrap {
  border: 1px solid #ddd;
  display: flex;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  flex-direction: row;
}

.input-icon {
  color: white;
  border-top-right-radius: 4px;
  border: #79aec8 solid 1px;
  border-bottom-right-radius: 4px;
  background: #79aec8;
}

.input-with-icon {
  flex: 1;
}

.input-icon,
.input-with-icon {
  padding: 10px;
}
</style>

<script>
/* eslint-disable camelcase */

import { has, toPairs } from "lodash"
import { ApiCalls } from "@/assets/libs/services"
import RecordingExtra from "@/components/Modules/RecordingExtra"
import { mapGetters } from "vuex"
import { safeUpdateArray } from "@/assets/libs/utils"

export default {
  components: {
    RecordingExtra
  },
  data () {
    return {
      loading: false,
      error: "",
      title: "",
      eligible: true,
      artist: "",
      isrc: "",
      fixationYear: "",
      fixationCountry: "",
      duration: "",
      type: "",
      releaseNames: [],
      releaseName: "",
      versionTitle: "",
      extras: [{ tag: null, language: null, value: null, canRemove: false, complete: false }],
    }
  },
  updated () {
    this.missingField = null
  },
  computed: {
    ...mapGetters("soundsys", ["countries", "srTypes"])
  },
  mounted () {
    this.mapSoundRecording()
  },
  methods: {
    addAlbumName (e) {
      if (this.releaseName.length && (e.type === "click" || e.key === "Enter")) {
        this.releaseNames = this.releaseNames.concat(this.releaseName)
        this.releaseName = ""
      }
    },

    removeReleaseName (releaseName) {
      this.releaseNames = safeUpdateArray(this.releaseNames, releaseName)
    },
    showRequired (field) {
      if (this.missingField === field) return "required"
      return " "
    },
    addExtra () {
      this.extras.push({ tag: null, language: null, value: null, canRemove: true, complete: false })
    },
    removeExtra (key) {
      if (this.extras.length > 1) {
        this.extras.splice(key, 1)
      } else {
        this.extras = [{ tag: null, language: null, value: null, canRemove: false, complete: false }]
      }
    },
    saveRecord () {
      const missingRequiredFields = ["title", "artist", "type"].some(
        (field, index, arr) => {
          const currentField = this[field]
          if (!currentField) {
            this.missingField = arr[index]
            this.$forceUpdate()
          }
          return !currentField
        }
      )
      if (missingRequiredFields) {
        const decamelizedField = this.$options.filters.decamelize(
          this.missingField
        )
        return this.$toasted.global.toast(`${ this.$t("missing_required_fields") } ${ decamelizedField }"`)
      }
      this.loading = true
      const duration = this.$options.filters.toSeconds(this.duration)
      if (duration === "invalid") {
        this.loading = false
        this.$toasted.global.toast(this.$t("invalid_time_format"))
      }

      let title = this.title
      if (this.versionTitle && this.versionTitle.trim() !== "") {
        title += ` (${this.versionTitle})`
      }
      let url = "soundrecordings/"
      if (this.modalType === "usages") {
        url = `soundrecordingusages/${this.soundRecording.id}/create_placeholder/`
      }

      const srData = {
        title: title,
        artist: this.artist,
        isrc: this.isrc,
        fixation_year: this.fixationYear,
        fixation_country: this.fixationCountry,
        id: this.soundRecording.id,
        release_names: this.releaseNames,
        type: this.$options.filters.get(this.type, "code", ""),
        duration,
        extras: this.extras.filter(extra => extra.complete)
      }
      if (this.modalType === "usages") {
        srData.eligible = this.eligible
      }

      ApiCalls.saveRecord(srData, url)
        .then(() => {
          this.loading = false
          this.$emit("close", this.$refs.recordingDetailsModal)
          this.$toasted.global.toast(this.$t("sound_recording_saved"))
        })
        .catch(error => {
          this.loading = false
          let message = null
          const { data } = error.response
          // Create message to display in toast
          if (has(data, "message")) {
            message = data.message
          } else {
            message = toPairs(data).map(pair => `${pair[0]}: ${pair[1].join(" ")}`).join(" ")
          }
          this.$toasted.global.toast(message)
        })
    },
    mapSoundRecording () {
      this.releaseNames = this.soundRecording.release_names || []
      const {
        title,
        artist,
        fixation_year,
        fixation_country,
        type,
        duration,
        isrc,
        extras,
      } = this.soundRecording

      this.title = title
      this.artist = artist
      this.isrc = isrc
      this.fixationYear = fixation_year
      this.fixationCountry = fixation_country
      this.type = this.srTypes.find(t => t.code === type)
      if (extras && extras.length) this.extras = extras
      this.duration = duration
        ? this.$options.filters.normalizeSeconds(duration)
        : ""
    }
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    modalType: {
      type: String,
      default: "soundrecording"
    },
    soundRecording: {
      type: Object,
      required: true
    }
  }
}
</script>
